/**
 * RDelta Theme JavaScript
 * Main theme functionality
 */

(function() {
  'use strict';

  // ============================================
  // Utility Functions
  // ============================================

  function getFocusableElements(container) {
    return Array.from(
      container.querySelectorAll(
        'summary, a[href], button:enabled, [tabindex]:not([tabindex^="-"]), input:enabled, select:enabled, textarea:enabled, object, iframe[src]'
      )
    );
  }

  function trapFocus(container, elementToFocus = container) {
    var elements = getFocusableElements(container);
    var first = elements[0];
    var last = elements[elements.length - 1];

    function focusTrap(e) {
      if (e.shiftKey) {
        if (document.activeElement === first || document.activeElement === container) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    }

    container.addEventListener('keydown', focusTrap);
    elementToFocus.focus();
  }

  function pauseAllMedia() {
    document.querySelectorAll('.js-youtube').forEach(video => {
      video.contentWindow.postMessage('{"event":"command","func":"' + 'pauseVideo' + '","args":""}', '*');
    });
    document.querySelectorAll('.js-vimeo').forEach(video => {
      video.contentWindow.postMessage('{"method":"pause"}', '*');
    });
    document.querySelectorAll('video').forEach(video => video.pause());
    document.querySelectorAll('product-model').forEach(model => {
      if (model.modelViewerUI) model.modelViewerUI.pause();
    });
  }

  function removeTrapFocus(elementToFocus = null) {
    document.removeEventListener('keydown', trapFocus);
    if (elementToFocus) elementToFocus.focus();
  }

  // ============================================
  // Cart Functions
  // ============================================

  function updateCartCount(count) {
    const cartCountElements = document.querySelectorAll('.cart-count-bubble, .cart-count-number');
    cartCountElements.forEach(el => {
      el.textContent = count;
    });
  }

  function getCart() {
    return fetch(window.routes.cart_url + '.js')
      .then(response => response.json())
      .catch((e) => {
        console.error('Error getting cart:', e);
        return null;
      });
  }

  function updateCartTotals(cart) {
    const cartTotals = document.querySelector('.cart-footer__totals');
    if (cartTotals && cart) {
      cartTotals.querySelector('.price').innerHTML = Shopify.formatMoney(cart.total_price, Shopify.money_format);
    }
  }

  // ============================================
  // Mobile Navigation
  // ============================================

  class MobileNav {
    constructor() {
      this.details = document.querySelectorAll('.menu-drawer-container details');
      this.mainDetailsToggle = document.querySelector('.menu-drawer__inner-submenu');
      
      this.bindEvents();
    }

    bindEvents() {
      this.details.forEach(details => {
        details.addEventListener('mouseenter', this.onMouseEnter.bind(this));
        details.addEventListener('mouseleave', this.onMouseLeave.bind(this));
        
        details.querySelector('summary').addEventListener('click', this.onSummaryClick.bind(this));
        details.addEventListener('keyup', this.onKeyUp.bind(this));
      });

      document.addEventListener('keyup', this.onKeyUp.bind(this));
      document.addEventListener('focusout', this.onFocusOut.bind(this));

      // Close menu drawer when clicking outside
      document.addEventListener('click', (e) => {
        const menuDrawer = document.querySelector('.menu-drawer');
        if (menuDrawer && !menuDrawer.contains(e.target)) {
          const summary = document.querySelector('.menu-drawer-container summary');
          if (summary) summary.removeAttribute('open');
        }
      });
    }

    onKeyUp(event) {
      if (event.code.toUpperCase() !== 'ESCAPE') return;

      const openDetails = event.target.closest('details[open]');
      if (!openDetails) return;

      openDetails === this.mainDetailsToggle ? this.closeMenuDrawer(this.mainDetailsToggle.querySelector('summary')) : this.closeSubmenu(openDetails);
    }

    onSummaryClick(event) {
      event.preventDefault();
      event.target.closest('details').classList.toggle('opened');
      
      // Trap focus in submenu
      if (event.target.closest('details').hasAttribute('open')) {
        setTimeout(() => {
          const submenu = event.target.nextElementSibling;
          trapFocus(submenu, submenu.querySelector('button'));
        }, 50);
      }
    }

    onMouseEnter(event) {
      // Only for desktop
      if (window.matchMedia('(min-width: 990px)').matches) {
        event.target.closest('details').setAttribute('open', true);
      }
    }

    onMouseLeave(event) {
      if (window.matchMedia('(min-width: 990px)').matches) {
        const parent = event.target.closest('details');
        setTimeout(() => {
          if (!parent.contains(document.activeElement)) {
            parent.removeAttribute('open');
          }
        }, 200);
      }
    }

    onFocusOut() {
      setTimeout(() => {
        if (document.activeElement.closest('.menu-drawer') === null && document.activeElement.closest('.menu-drawer__inner-submenu') === null) {
          this.closeMenuDrawer(document.activeElement);
        }
      });
    }

    closeSubmenu(details) {
      details.removeAttribute('open');
      details.querySelector('summary').setAttribute('aria-expanded', false);
    }

    closeMenuDrawer(summary) {
      this.mainDetailsToggle.removeAttribute('open');
      summary.removeAttribute('open');
      removeTrapFocus(summary);
    }
  }

  // ============================================
  // Sticky Header
  // ============================================

  class StickyHeader {
    constructor() {
      this.header = document.querySelector('.header-wrapper');
      this.headerBounds = {};
      this.currentScrollTop = 0;
      this.preventReveal = false;
      
      this.bindEvents();
    }

    bindEvents() {
      this.onScroll = this.onScroll.bind(this);
      window.addEventListener('scroll', this.onScroll, false);
      
      this.onScroll();
    }

    onScroll() {
      const currentScrollTop = window.scrollY || document.documentElement.scrollTop;

      if (this.currentScrollTop > currentScrollTop && currentScrollTop > 0) {
        // Scrolling down
        if (!this.preventReveal) {
          this.header.classList.remove('hidden', 'scrolled-down');
        }
      } else if (this.currentScrollTop < currentScrollTop) {
        // Scrolling up
        this.header.classList.add('scrolled-down');
        
        if (this.currentScrollTop > 200) {
          this.header.classList.remove('hidden');
        }
      }

      if (currentScrollTop <= 0) {
        this.header.classList.remove('scrolled-down');
      }

      this.currentScrollTop = currentScrollTop;
    }
  }

  // ============================================
  // Product Gallery
  // ============================================

  class ProductGallery {
    constructor() {
      this.thumbnails = document.querySelectorAll('.pdp-thumb');
      this.init();
    }

    init() {
      this.thumbnails.forEach(thumb => {
        thumb.addEventListener('click', (e) => {
          const thumbImg = thumb.querySelector('img');
          if (!thumbImg) return;
          
          const mainImg = document.getElementById('mainImg');
          if (mainImg) {
            const fullSrc = thumb.getAttribute('data-full-src');
            if (fullSrc) {
              mainImg.srcset = '';
              mainImg.src = fullSrc;
            }
          }
          
          this.thumbnails.forEach(t => t.classList.remove('active'));
          thumb.classList.add('active');
        });
      });
    }
  }

  // ============================================
  // Facets (Filtering)
  // ============================================

  class Facets {
    constructor() {
      this.filterForm = document.querySelector('.facets__form');
      this.sortSelect = document.querySelector('#SortBy');
      
      this.bindEvents();
    }

    bindEvents() {
      if (this.filterForm) {
        const checkboxes = this.filterForm.querySelectorAll('input[type="checkbox"]');
        checkboxes.forEach(cb => {
          cb.addEventListener('change', () => this.submitForm());
        });

        const priceInputs = this.filterForm.querySelectorAll('.filter-group__price input');
        priceInputs.forEach(input => {
          input.addEventListener('change', () => this.submitForm());
        });

        const priceBtns = this.filterForm.querySelectorAll('.price-btn');
        priceBtns.forEach(btn => {
          btn.addEventListener('click', (e) => {
            e.preventDefault();
            const field = btn.closest('.filter-group__price-field');
            const input = field.querySelector('input');
            const isIncrement = btn.classList.contains('price-btn--inc');
            let currentValue = parseInt(input.value, 10) || 0;
            
            if (isIncrement) {
              currentValue = currentValue + 1;
            } else {
              currentValue = Math.max(0, currentValue - 1);
            }
            
            input.value = currentValue;
            this.submitForm();
          });
        });
      }

      if (this.sortSelect) {
        this.sortSelect.addEventListener('change', () => this.handleSort());
      }
    }

    submitForm() {
      const form = this.filterForm;
      if (form) {
        form.submit();
      }
    }
  }

  // ============================================
  // Quantity Selector
  // ============================================

  class QuantitySelector {
    constructor() {
      this.init();
    }

    init() {
      document.querySelectorAll('.quantity').forEach(qty => {
        const input = qty.querySelector('.quantity__input');
        const minusBtn = qty.querySelector('.quantity__button--minus');
        const plusBtn = qty.querySelector('.quantity__button--plus');

        if (minusBtn) {
          minusBtn.addEventListener('click', () => {
            if (input.value > 1) {
              input.value = parseInt(input.value) - 1;
              input.dispatchEvent(new Event('change'));
            }
          });
        }

        if (plusBtn) {
          plusBtn.addEventListener('click', () => {
            input.value = parseInt(input.value) + 1;
            input.dispatchEvent(new Event('change'));
          });
        }
      });
    }
  }

  // ============================================
  // Cart Quantity Updates
  // ============================================

  function initCartQuantityUpdates() {
    const cartForm = document.querySelector('.cart-form');
    if (!cartForm) return;

    const quantityInputs = cartForm.querySelectorAll('.quantity__input');
    quantityInputs.forEach(input => {
      input.addEventListener('change', () => {
        cartForm.submit();
      });
    });
  }

  // ============================================
  // Video Handling
  // ============================================

  function handleVideoAutoPlay() {
    // Pause videos when not in viewport
    const videos = document.querySelectorAll('video');
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (!entry.isIntersecting) {
          entry.target.pause();
        } else {
          if (entry.target.hasAttribute('autoplay')) {
            entry.target.play();
          }
        }
      });
    }, { threshold: 0.5 });

    videos.forEach(video => observer.observe(video));
  }

  // ============================================
  // Smooth Scroll
  // ============================================

  function initSmoothScroll() {
    document.querySelectorAll('a[href^="#"]').forEach(anchor => {
      anchor.addEventListener('click', function(e) {
        const href = this.getAttribute('href');
        if (href === '#') return;
        
        const target = document.querySelector(href);
        if (target) {
          e.preventDefault();
          target.scrollIntoView({ behavior: 'smooth' });
        }
      });
    });
  }

  // ============================================
  // Initialize on DOM Ready
  // ============================================

  document.addEventListener('DOMContentLoaded', () => {
    // Initialize mobile navigation
    if (document.querySelector('.menu-drawer-container')) {
      new MobileNav();
    }

    // Initialize sticky header
    if (document.querySelector('.header-wrapper')) {
      new StickyHeader();
    }

    // Initialize product gallery
    if (document.querySelector('.pdp-gallery')) {
      new ProductGallery();
    }

    // Initialize facets
    if (document.querySelector('.facets__form')) {
      new Facets();
    }

    // Initialize quantity selectors
    if (document.querySelector('.quantity')) {
      new QuantitySelector();
    }

    // Initialize cart quantity updates
    initCartQuantityUpdates();

    // Initialize video handling
    handleVideoAutoPlay();

    // Initialize smooth scroll
    initSmoothScroll();
  });

  // ============================================
  // Window Events
  // ============================================

  // Pause media when going to cart
  window.addEventListener('beforeunload', () => {
    pauseAllMedia();
  });

})();
